// src/pages/admin/Audience.js
import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../../supabaseClient";
import {
  Search,
  MapPin,
  School,
  User,
  Plus,
  ChevronDown,
  ChevronUp,
  Check,
  Filter,
  Ruler,
  Weight,
  Users,
  Send,
  ChevronRight,
  Briefcase
} from "lucide-react";

// --- HELPER: Parse Address for Privacy ---
const formatLocation = (fullAddress) => {
  if (!fullAddress) return "Location N/A";
  const parts = fullAddress.split(",");
  if (parts.length >= 3) {
    const city = parts[parts.length - 2]?.trim();
    const stateZip = parts[parts.length - 1]?.trim();
    const state = stateZip?.split(" ")[0];
    return `${city}, ${state}`;
  }
  return fullAddress;
};

export default function Audience() {
  // --- STATE MANAGEMENT ---
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [adminPrograms, setAdminPrograms] = useState([]);
  const [role, setRole] = useState(null);

  const [followers, setFollowers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [rosteredTeamsByUser, setRosteredTeamsByUser] = useState({});
  const [loading, setLoading] = useState(true);

  // UI States
  const [searchQuery, setSearchQuery] = useState("");
  const [gradeFilter, setGradeFilter] = useState("All");
  const [sortBy, setSortBy] = useState("name_asc");
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [inviteStatus, setInviteStatus] = useState({});

  // --- 1. FETCH ADMIN PROGRAMS (INITIAL LOAD) ---
  useEffect(() => {
    async function fetchAdminPrograms() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Fetch role + programs in parallel to reduce initial TTFB.
        const [roleRes, programRes] = await Promise.all([
          supabase
            .from("users")
            .select("role")
            .eq("id", user.id)
            .single(),
          supabase
            .from("programs")
            .select("id, name")
            .eq("administrator", user.id)
            .order("name")
        ]);

        if (roleRes.data?.role) setRole(roleRes.data.role);

        const { data, error } = programRes;

        if (!error && data && data.length > 0) {
          setAdminPrograms(data);
          // Default to the first program in the list
          setSelectedProgramId(data[0].id);
        } else {
          setLoading(false); // Stop loading if no programs found
        }
      } catch (err) {
        console.error("Error fetching admin programs:", err);
        setLoading(false);
      }
    }
    fetchAdminPrograms();
  }, []);

  // --- 2. FETCH FOLLOWERS & TEAMS (OPTIMIZED: Parallel & Lean Selects) ---
  useEffect(() => {
    async function fetchData() {
      if (!selectedProgramId || !role) return;

      try {
        setLoading(true);
        // Avoid unnecessary teams query for SkillsTrainer view.
        const followerPromise = supabase
          .from("user_program_follows")
          .select(`
            user_id,
            users!inner (
              id, full_name, profile_img, gender, grade,
              user_school, home_location, height_feet,
              height_inches, weight, email
            )
          `)
          .eq("program_id", selectedProgramId);

        const teamPromise = role === "SkillsTrainer"
          ? Promise.resolve({ data: [], error: null })
          : supabase
              .from("teams")
              .select("id, name")
              .eq("program_id", selectedProgramId)
              .order("name");

        const [followerRes, teamRes] = await Promise.all([followerPromise, teamPromise]);

        if (followerRes.error) throw followerRes.error;
        if (teamRes.error) throw teamRes.error;

        // Flatten + de-dupe user data by id.
        const validUsers = Array.from(
          new Map(
            (followerRes.data || [])
              .map((f) => f.users)
              .filter(Boolean)
              .map((u) => [u.id, u])
          ).values()
        );

        setFollowers(validUsers);
        const programTeams = role === "SkillsTrainer" ? [] : (teamRes.data || []);
        setTeams(programTeams);

        if (role === "SkillsTrainer" || validUsers.length === 0 || programTeams.length === 0) {
          setRosteredTeamsByUser({});
        } else {
          const teamIds = programTeams.map((team) => team.id);
          const userIds = validUsers.map((u) => u.id);
          const { data: rosterRows, error: rosterError } = await supabase
            .from("rostered_users")
            .select("user_id, team_id")
            .in("user_id", userIds)
            .in("team_id", teamIds);

          if (rosterError) throw rosterError;

          const teamNameById = new Map(programTeams.map((team) => [team.id, team.name]));
          const byUser = {};
          (rosterRows || []).forEach((row) => {
            const teamName = teamNameById.get(row.team_id);
            if (!teamName) return;
            if (!byUser[row.user_id]) byUser[row.user_id] = [];
            if (!byUser[row.user_id].some((team) => team.id === row.team_id)) {
              byUser[row.user_id].push({ id: row.team_id, name: teamName });
            }
          });

          setRosteredTeamsByUser(byUser);
        }

      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [selectedProgramId, role]);

  // --- HANDLE INVITE ---
  const handleInvite = async (user, teamId) => {
    const statusKey = `${user.id}-${teamId}`;
    setInviteStatus(prev => ({ ...prev, [statusKey]: "loading" }));

    try {
      if (!user.email) {
        throw new Error("This athlete does not have an email on file.");
      }

      // 1. Check if already Rostered
      const { data: rosterCheck, error: rosterError } = await supabase
        .from("rostered_users")
        .select("id")
        .eq("user_id", user.id)
        .eq("team_id", teamId)
        .maybeSingle();

      if (rosterError) throw rosterError;
      if (rosterCheck) {
        alert("User is already on this roster.");
        setInviteStatus(prev => ({ ...prev, [statusKey]: "error" }));
        return;
      }

      // 2. Check if Invite Pending
      const { data: inviteCheck, error: inviteError } = await supabase
        .from("player_invites")
        .select("id, status")
        .eq("team_id", teamId)
        .ilike("email", user.email)
        .limit(1);

      if (inviteError) throw inviteError;
      const existingInvite = inviteCheck?.[0];
      if (existingInvite?.status === "pending") {
        alert("Invite already pending for this team.");
        setInviteStatus(prev => ({ ...prev, [statusKey]: "sent" }));
        return;
      }
      if (existingInvite?.status === "accepted") {
        alert("This athlete already accepted an invite for this team.");
        setInviteStatus(prev => ({ ...prev, [statusKey]: "sent" }));
        return;
      }
      if (existingInvite) {
        const { error: resendError } = await supabase
          .from("player_invites")
          .update({ status: "pending" })
          .eq("id", existingInvite.id);

        if (resendError) throw resendError;
        setInviteStatus(prev => ({ ...prev, [statusKey]: "sent" }));
        return;
      }

      // 3. Create Invite
      const { error: insertError } = await supabase
        .from("player_invites")
        .insert({
          program_id: selectedProgramId,
          team_id: teamId,
          email: user.email,
          status: "pending"
        });

      if (insertError) throw insertError;

      setInviteStatus(prev => ({ ...prev, [statusKey]: "sent" }));

    } catch (err) {
      console.error("Invite error:", err);
      setInviteStatus(prev => ({ ...prev, [statusKey]: "error" }));
      alert(`Failed to send invite${err?.message ? `: ${err.message}` : "."}`);
    }
  };

  const toggleExpand = (userId) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  // --- FILTER LOGIC ---
  const filteredFollowers = useMemo(() => {
    const s = searchQuery.toLowerCase();
    const filtered = followers.filter((user) => {
      const matchesSearch = (user.full_name || "").toLowerCase().includes(s) ||
        (user.user_school || "").toLowerCase().includes(s);

      const matchesGrade = gradeFilter === "All" || (user.grade && user.grade.toString() === gradeFilter);

      return matchesSearch && matchesGrade;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (sortBy === "name_desc") {
        return String(b.full_name || "").localeCompare(String(a.full_name || ""));
      }
      if (sortBy === "grade_desc") {
        return Number(b.grade || 0) - Number(a.grade || 0);
      }
      if (sortBy === "grade_asc") {
        return Number(a.grade || 0) - Number(b.grade || 0);
      }
      return String(a.full_name || "").localeCompare(String(b.full_name || ""));
    });
    return sorted;
  }, [followers, gradeFilter, searchQuery, sortBy]);

  return (
    <div className="min-h-screen bg-gray-100 pb-20 font-sans">

      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm sticky top-0 z-20 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
            <Users size={20} />
          </div>
          {/* Updated Language */}
          <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">
            {role === "SkillsTrainer" ? "Clients" : "Prospective Athletes"}
          </h1>
        </div>

        {/* --- PROGRAM SWITCHER DROPDOWN --- */}
        {adminPrograms.length > 0 && (
          <div className="relative min-w-[250px]">
            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
            <select
              value={selectedProgramId || ""}
              onChange={(e) => setSelectedProgramId(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-bold text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none cursor-pointer transition-all hover:bg-white hover:shadow-sm"
            >
              {adminPrograms.map(prog => (
                <option key={prog.id} value={prog.id}>
                  {prog.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-6">

        {/* --- CONTROLS --- */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder={role === "SkillsTrainer" ? "Search client or school..." : "Search athlete or school..."}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-semibold focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all placeholder-gray-400"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {/* Filter */}
            <div className="relative min-w-[140px]">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <select
                className="w-full pl-9 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-bold focus:ring-2 focus:ring-blue-100 focus:border-blue-300 appearance-none cursor-pointer"
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
              >
                <option value="All">All Grades</option>
                {[9, 10, 11, 12].map(g => (
                  <option key={g} value={g}>Grade {g}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* --- LIST --- */}
        {filteredFollowers.length > 0 && (
          <div className="hidden lg:grid grid-cols-12 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
            <div className="col-span-3">Athlete</div>
            <div className="col-span-2">Team</div>
            <div className="col-span-1 text-center">Height</div>
            <div className="col-span-1 text-center">Weight</div>
            <div className="col-span-2">School</div>
            <div className="col-span-1 text-center">Grade</div>
            <div className="col-span-1 text-center">Location</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
        )}
        {loading && filteredFollowers.length === 0 ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gray-200" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-40 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-24" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredFollowers.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <User size={48} className="mx-auto mb-2 opacity-20" />
            <p className="font-medium">
              {role === "SkillsTrainer" ? "No clients found." : "No prospective athletes found."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filteredFollowers.map((user) => {
              const userTeams = rosteredTeamsByUser[user.id] || [];
              const userTeamLabel = userTeams.length > 1 ? `${userTeams.length} Teams` : userTeams[0]?.name;
              return (
                <div
                  key={user.id}
                  className={`bg-white rounded-xl border transition-all duration-200 ${expandedUserId === user.id ? "border-blue-400 shadow-md" : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                    }`}
                >
                {/* Main Row Container */}
                <div className="flex flex-col lg:grid lg:grid-cols-12 lg:items-center p-4 gap-4 lg:gap-0">

                  {/* 1. Athlete Profile & Primary Info */}
                  <div className="flex items-center gap-3 min-w-[220px] lg:col-span-3">
                    <div className="min-w-0">
                      <h3 className="font-bold text-gray-900 truncate">{user.full_name || "Unknown User"}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <MapPin size={12} /> {formatLocation(user.home_location)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Team (desktop) */}
                  <div className="hidden lg:flex lg:col-span-2 items-center text-sm font-semibold text-gray-700 border-l border-gray-100 px-4">
                    {role !== "SkillsTrainer"
                      ? (userTeams.length > 0 ? userTeamLabel : "--")
                      : "--"}
                  </div>

                  {/* 2. Physicals & Stats (Hidden on small screens, shown as row on MD+) */}
                  <div className="hidden lg:flex lg:col-span-1 items-center justify-center border-l border-gray-100">
                    <div className="text-center">
                      <p className="text-[10px] uppercase text-gray-400 font-bold mb-0.5">Height</p>
                      <p className="text-sm font-semibold text-gray-700">
                        {user.height_feet ? `${user.height_feet}'${user.height_inches || 0}"` : "--"}
                      </p>
                    </div>
                  </div>
                  <div className="hidden lg:flex lg:col-span-1 items-center justify-center border-l border-gray-100">
                    <div className="text-center">
                      <p className="text-[10px] uppercase text-gray-400 font-bold mb-0.5">Weight</p>
                      <p className="text-sm font-semibold text-gray-700">
                        {user.weight ? `${user.weight} lbs` : "--"}
                      </p>
                    </div>
                  </div>

                  {/* 3. School Info */}
                  <div className="hidden lg:block lg:col-span-2 px-4 border-l border-gray-100">
                    <div className="flex items-center gap-2 text-gray-600">
                      <School size={16} className="text-gray-300" />
                      <span className="text-sm font-medium truncate">{user.user_school || "No School Listed"}</span>
                    </div>
                  </div>

                  {/* Grade (desktop) */}
                  <div className="hidden lg:flex lg:col-span-1 items-center justify-center text-xs font-bold text-gray-700 border-l border-gray-100">
                    {user.grade ? `${user.grade}` : "--"}
                  </div>

                  {/* Location (desktop) */}
                  <div className="hidden lg:flex lg:col-span-1 items-center justify-center text-xs font-semibold text-gray-500 border-l border-gray-100">
                    {formatLocation(user.home_location)}
                  </div>

                  {/* 4. Action Area */}
                  <div className="flex items-center justify-end gap-2 ml-auto lg:col-span-1">
                    {role !== "SkillsTrainer" ? (
                      userTeams.length > 0 ? (
                        <div className="px-3 py-2 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          On Team: {userTeamLabel}
                        </div>
                      ) : (
                      <button
                        onClick={() => toggleExpand(user.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${expandedUserId === user.id
                            ? "bg-gray-900 text-white"
                            : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm shadow-blue-200"
                          }`}
                      >
                        {expandedUserId === user.id ? "Close" : "Invite to Team"}
                        {expandedUserId === user.id ? <ChevronUp size={16} /> : <Plus size={16} />}
                      </button>
                      )
                    ) : (
                      <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors">
                        <ChevronRight size={20} />
                      </button>
                    )}
                  </div>
                </div>

                {/* --- EXPANDABLE TEAM SELECTION (Full Width Row) --- */}
                {expandedUserId === user.id && role !== "SkillsTrainer" && (
                  <div className="bg-gray-50 border-t border-gray-100 p-4 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="max-w-2xl">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Available Teams</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {teams.map((team) => {
                          const isRostered = (rosteredTeamsByUser[user.id] || []).some((rosteredTeam) => rosteredTeam.id === team.id);
                          const status = inviteStatus[`${user.id}-${team.id}`];
                          const isSent = status === "sent" || isRostered;
                          return (
                            <button
                              key={team.id}
                              onClick={() => !isSent && handleInvite(user, team.id)}
                              disabled={isSent}
                              className={`flex items-center justify-between p-3 rounded-lg border text-xs font-bold transition-all ${isSent ? "bg-green-50 border-green-200 text-green-700" : "bg-white border-gray-200 hover:border-blue-400"
                                }`}
                            >
                              <span className="truncate mr-2">{team.name}</span>
                              {isSent ? <Check size={14} /> : <Send size={12} className="text-blue-500" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
